import { Box, CircularProgress } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import axios, { isAxiosError } from "axios";
import { objectToCamel } from "ts-case-convert";
import subdomain from "lib/subdomain";
import TokenService from "services/token.service";
import { API_BASE_URL } from "services/api.service";
import { AuthenticationContext } from "hooks/useAuthentication";
import { useCurrentUser, type User } from "hooks/useCurrentUser";

type AuthStatus = "pending" | "resolved";

/**
 * Resolves auth state before rendering the app: if no token, treats as
 * unauthenticated; if token exists, validates via GET /current_user without
 * triggering the global 401 redirect. Holds rendering (shows loader) until
 * resolved so we never flash protected content then snap to login.
 */
export function AuthResolver({ children }: { children: React.ReactNode }) {
  const { setLoggedIn } = useContext(AuthenticationContext);
  const { setCurrentUser } = useCurrentUser();
  const [authStatus, setAuthStatus] = useState<AuthStatus>("pending");

  const resolveAuth = useCallback(() => {
    const token = TokenService.get();
    if (!token) {
      setLoggedIn(false);
      setAuthStatus("resolved");
      return;
    }

    const client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
    });
    const organizationSubdomain = subdomain();
    client
      .get("/current_user", {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          ...(organizationSubdomain && {
            "organization-subdomain": organizationSubdomain,
          }),
        },
      })
      .then((response) => {
        const data = objectToCamel(response.data) as { user: unknown };
        if (data?.user) {
          setCurrentUser(data.user as User);
        }
        setLoggedIn(true);
        setAuthStatus("resolved");
      })
      .catch((err: unknown) => {
        TokenService.remove();
        setLoggedIn(false);
        let nextPath = "/login";
        if (
          isAxiosError(err) &&
          err.response?.data &&
          typeof err.response.data === "object" &&
          "error" in err.response.data &&
          (err.response.data as { error?: string }).error ===
            "organization_mismatch"
        ) {
          nextPath = "/login?org_mismatch=1";
        }
        window.location.assign(nextPath);
        setAuthStatus("resolved");
      });
  }, [setLoggedIn, setCurrentUser]);

  useEffect(() => {
    resolveAuth();
  }, [resolveAuth]);

  if (authStatus === "pending") {
    return (
      <Box
        width="100%"
        height="100vh"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress sx={{ marginTop: 3 }} />
      </Box>
    );
  }

  return <>{children}</>;
}
